/**
 * Active-heading tracker for the TOC sidebar (Phase 8).
 *
 * Watches a scroll container and reports the id of the heading that is
 * currently "at the top" of the viewport — i.e. the last heading whose top
 * edge has crossed an 80px offset below the container's top. That offset
 * exists so the active heading switches a beat *before* its top reaches
 * the very top of the viewport, which feels more natural while scrolling.
 *
 * We poll with requestAnimationFrame on scroll rather than using
 * IntersectionObserver because IO's `rootMargin` + threshold model is
 * tuned for "is this element visible at all" — getting "the most recent
 * heading above the fold" out of it requires fighting the API. rAF on a
 * passive scroll listener gives us deterministic "what's at the top right
 * now" with zero observer bookkeeping.
 *
 * Callback fires only on transitions (id !== current). The empty-document
 * case clears the highlight by emitting null exactly once.
 *
 * Teardown returns from the bind effect — cancels any pending rAF and
 * removes the scroll listener so the Svelte effect can swap containers
 * (mode changes that re-render Preview) without leaking handlers.
 */

export function trackActiveHeading(
  scrollContainer: HTMLElement,
  cb: (id: string | null) => void,
): () => void {
  let current: string | null = null
  let raf = 0

  function update(): void {
    raf = 0
    // Re-query each tick — Preview re-renders may swap the heading set
    // (new doc, filtered, etc.) and we don't want a stale list cached.
    const headings = Array.from(
      scrollContainer.querySelectorAll<HTMLElement>(
        'h1[id], h2[id], h3[id], h4[id], h5[id], h6[id]',
      ),
    )
    if (headings.length === 0) {
      if (current !== null) {
        current = null
        cb(null)
      }
      return
    }
    // 80px below the container top is the trigger line. A heading is
    // "active" when its top is at or above that line — i.e. it has
    // already scrolled past the trigger. We walk in document order and
    // keep the last match; `break` once we see a heading still below the
    // line, since headings are in vertical order.
    const containerTop = scrollContainer.getBoundingClientRect().top + 80
    let best: HTMLElement | null = null
    for (const h of headings) {
      if (h.getBoundingClientRect().top <= containerTop) best = h
      else break
    }
    // Fallback to the first heading when none has crossed yet — keeps
    // the TOC pointing at the start of the doc instead of going blank
    // before the user has scrolled at all.
    const next = best?.id ?? headings[0]!.id
    if (next !== current) {
      current = next
      cb(next)
    }
  }

  function onScroll(): void {
    if (raf) return
    raf = requestAnimationFrame(update)
  }

  scrollContainer.addEventListener('scroll', onScroll, { passive: true })
  // Prime the highlight immediately so the sidebar isn't blank until the
  // user first scrolls.
  update()

  return () => {
    if (raf) cancelAnimationFrame(raf)
    scrollContainer.removeEventListener('scroll', onScroll)
  }
}
